import React, { useState } from "react";
import { Phone, Mail, MapPin, Send, CheckCircle2, ImagePlus, X, Loader2 } from "lucide-react";
import { submitContact, uploadCustomCakeImage } from "../lib/api";
import Seo from "../components/Seo";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const MAX_IMAGES = 5;

const Contact = () => {
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState([]); // {url, path}
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (images.length + files.length > MAX_IMAGES) {
      setError(`You can upload up to ${MAX_IMAGES} sample pictures.`);
      return;
    }
    setError("");
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await uploadCustomCakeImage(fd);
        const absUrl = `${BACKEND}${res.url}`;
        setImages((prev) => [...prev, { url: absUrl, path: res.path }]);
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Could not upload image. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (path) => setImages((prev) => prev.filter((i) => i.path !== path));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await submitContact({ ...form, images: images.map((i) => i.url) });
      setSent(true);
      setForm({ name: "", email: "", phone: "", message: "" });
      setImages([]);
    } catch (err) {
      console.error("Failed to submit contact form:", err);
      setError("Something went wrong sending your message. Please try again or WhatsApp us.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-12">
      <Seo
        path="/contact"
        title="Contact Us & Custom Cake Orders in Noida"
        description="Get in touch with Globaltreats for custom cake orders in Noida & Greater Noida. Call +91 92649 71235 or send your reference designs for a bespoke, hand-decorated cake."
      />
      <div className="text-center mb-12">
        <h1 className="font-display text-4xl font-bold text-[#3A2417]">Get in Touch</h1>
        <p className="text-[#B07A5C] mt-2 max-w-lg mx-auto">Have a custom cake idea or a question? We'd love to hear from you.</p>
      </div>
      <div className="grid md:grid-cols-2 gap-10">
        <div className="space-y-6">
          {[{ icon: Phone, t: "Call Us", s: "+91 92649 71235", href: "tel:+919264971235" }, { icon: Mail, t: "Email Us", s: "globaltreatstradingllp@gmail.com", href: "mailto:globaltreatstradingllp@gmail.com" }, { icon: MapPin, t: "Address", s: "Verona, Mahagun Moderne, Sector 78, Noida", href: null }, { icon: MapPin, t: "Store", s: "Globaltreats Trading LLP, Main Ghijjore Road, 1st Floor, Near TVS Showroom, Sector 53, Noida", href: null }].map((c, i) => (
            <a key={i} href={c.href || undefined} className="flex items-center gap-4 bg-white rounded-2xl p-5 bakery-shadow hover:bakery-shadow-lg transition-shadow">
              <div className="w-12 h-12 rounded-full bg-[#F7DCE4] grid place-items-center shrink-0"><c.icon size={22} className="text-[#D46A8A]" /></div>
              <div><div className="font-semibold text-[#3A2417]">{c.t}</div><div className="text-sm text-[#6B4226] break-all">{c.s}</div></div>
            </a>
          ))}
          <div className="rounded-2xl overflow-hidden bakery-shadow">
            <img src="/assets/swati-bakery.png" alt="Globaltreats home bakery by Swati" className="w-full h-56 object-cover" />
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 md:p-8 bakery-shadow">
          {sent ? (
            <div className="text-center py-16">
              <CheckCircle2 size={48} className="text-[#6BA84F] mx-auto mb-4" />
              <h3 className="font-display text-xl font-bold text-[#3A2417]">Message Sent!</h3>
              <p className="text-sm text-[#6B4226] mt-2">Thanks for reaching out. Our team will get back to you shortly.</p>
              <button onClick={() => setSent(false)} className="mt-6 text-[#D46A8A] text-sm">Send another message</button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <h2 className="font-display text-xl font-semibold text-[#3A2417] mb-2">Send us a message</h2>
              {[{ k: "name", l: "Your Name", t: "text" }, { k: "email", l: "Email", t: "email" }, { k: "phone", l: "Phone", t: "tel" }].map((f) => (
                <div key={f.k}>
                  <label className="text-sm font-medium text-[#6B4226]">{f.l}</label>
                  <input type={f.t} value={form[f.k]} onChange={(e) => update(f.k, e.target.value)} required={f.k !== "phone"} className="w-full mt-1.5 h-11 px-4 rounded-xl bg-white border border-[#EADBCB] text-sm outline-none focus:border-[#D46A8A]" />
                </div>
              ))}
              <div>
                <label className="text-sm font-medium text-[#6B4226]">Message</label>
                <textarea value={form.message} onChange={(e) => update("message", e.target.value)} required rows={4} className="w-full mt-1.5 px-4 py-3 rounded-xl bg-white border border-[#EADBCB] text-sm outline-none focus:border-[#D46A8A] resize-none" />
              </div>

              <div data-testid="custom-cake-uploader">
                <label className="text-sm font-medium text-[#6B4226]">Sample pictures <span className="text-[#B07A5C] font-normal">(optional, up to {MAX_IMAGES})</span></label>
                <div className="flex flex-wrap gap-3 mt-2">
                  {images.map((img) => (
                    <div key={img.path} className="relative w-20 h-20 rounded-xl overflow-hidden border border-[#EADBCB]" data-testid={`sample-image-${img.path.split("/").pop()}`}>
                      <img src={img.url} alt="sample" className="w-full h-full object-cover" />
                      <button type="button" onClick={() => removeImage(img.path)} className="absolute top-0.5 right-0.5 w-5 h-5 grid place-items-center rounded-full bg-black/60 text-white" data-testid="sample-image-remove">
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                  {images.length < MAX_IMAGES && (
                    <label className={`w-20 h-20 rounded-xl border-2 border-dashed border-[#EADBCB] grid place-items-center cursor-pointer hover:border-[#D46A8A] text-[#B07A5C] ${uploading ? "opacity-60 pointer-events-none" : ""}`} data-testid="sample-image-add">
                      {uploading ? <Loader2 size={20} className="animate-spin" /> : <ImagePlus size={22} />}
                      <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" multiple onChange={handleFiles} className="hidden" data-testid="sample-image-input" />
                    </label>
                  )}
                </div>
                <p className="text-xs text-[#B07A5C] mt-1.5">Share reference designs so Swati can recreate your dream cake. JPG/PNG/WebP, max 8 MB each.</p>
              </div>

              {error && <p className="text-sm text-red-600">{error}</p>}
              <button type="submit" disabled={submitting || uploading} className="w-full h-12 rounded-full bg-[#D46A8A] text-white font-medium hover:bg-[#c25a7b] transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="contact-submit-btn">
                {submitting ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />} {submitting ? "Sending..." : "Send Message"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Contact;
